import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AxiosError } from 'axios';
import { RootState } from '../../store';
import bemClassName from '../../utils/bem';
import { formattedDate } from '../../utils/date';
import AppInput from '../../UI/AppInput';
import { TEXT } from '../../app_constants';

import './index.scss';
import Container from '../../UI/Container';
import AdvantagesSticker from '../../UI/AdvantagesSticker';
import CatalogData from '../../API/CatalodData';
import PrivateData from '../../API/PrivateData';
import Validate from '../../API/Validate';
import ApartamentPayment from '../../components/ApartamentPayment';
import { IApartamentPaymentData, IManager } from '../../components/ApartamentPayment/interface';
import Button from '../../UI/Button';
import Modal from '../../UI/Modal';

const payment = bemClassName('payment');

const Payment = () => {
  const [data, setData] = useState<IApartamentPaymentData>();
  const [accommodation, setAccommodation] = useState(false);
  const [dealId, setDealId] = useState<number | undefined>(undefined);
  const [productId, setProductId] = useState<string | undefined>(undefined);
  const isLogin = useSelector((state: RootState) => state.autorizationStatus.isLogin);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dealBegin, setDealBegin] = useState<string | undefined>(undefined);
  const [dealReserveBegin, setDealReserveBegin] = useState<string | undefined>(undefined);
  const [dealStageId, setDealStageId] = useState<string | undefined>(undefined);
  const [dealEnd, setDealEnd] = useState<string | undefined>(undefined);
  const [dealTotal, setDealTotal] = useState<number | undefined>(undefined);
  const [dealFullpay, setDealFullpay] = useState<number | undefined>(undefined);
  const [dealSurcharge, setDealSurcharge] = useState<number | undefined>(undefined);
  const [dealPrepay, setDealPrepay] = useState<number | undefined>(undefined);
  const [dealDiscount, setDealDiscount] = useState<number | undefined>(undefined);
  const [dealBonus, setDealBonus] = useState<number | undefined>(undefined);
  const [dealCashback, setDealCashback] = useState<boolean>(false);
  const [dealTimeout, setDealTimeout] = useState<string | undefined>(undefined);
  const [dealManager, setDealManager] = useState<IManager>();
  const [bonusCard, setBonusCard] = useState<string | undefined>(undefined);
  const [bonusCardIsValid, setBonusCardIsValid] = useState<boolean>();
  const isPaswordVerified = useSelector(
    (state: RootState) => state.autorizationStatus.isPaswordVerified,
  );
  const [isOpen, setOpen] = useState<boolean>(false);
  const bonus = useSelector((state: RootState) => state.autorizationStatus.bonus);

  const handleSetBonus = (value: string) => {
    setBonusCard(value);
  };

  useEffect(() => {
    if (!isPaswordVerified) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isPaswordVerified]);

  useEffect(() => {
    if (productId !== undefined) {
      document.title = 'Оплата';
      CatalogData.getApartament({ productId }).then((items) => {
        if (items.contactless && isPaswordVerified) {
          setAccommodation(true);
        }
        setData(items);
        setIsLoading(true);
      });
    }
  }, [productId]);

  useEffect(() => {
    if (dealCashback) {
      PrivateData.getPrivateUser().then((item) => {
        if (item.card) {
          setBonusCard(item.card.formatted);
          setBonusCardIsValid(item.card.valid);
        }
      });
    }
  }, [dealCashback]);

  useEffect(() => {
    const abortControllerBonusCard = new AbortController();
    if (bonusCard) {
      (async () => {
        try {
          const responseData = await Validate.checkBonusCard(
            bonusCard,
            abortControllerBonusCard.signal,
          );
          setBonusCardIsValid(true);
          if (responseData.formatted) {
            setBonusCard(responseData.formatted);
            PrivateData.getPrivateEditUserDate({ card: bonusCard });
          }
        } catch (error) {
          const err = error as Error | AxiosError;
          if (err.name === 'AbortError') {
            // return error;
          } else {
            setBonusCardIsValid(false);
          }
        }
      })();
    }
    return () => {
      abortControllerBonusCard.abort();
    };
  }, [bonusCard, bonusCardIsValid]);

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleAddDeposit = () => {
    if (dealId && data && data.deposit) {
      PrivateData.addPayment(dealId, data.deposit, 'deposit', dealCashback).then((item) => {
        if (item.redirect_url) {
          window.location.href = item.redirect_url;
        }
      });
    }
  };

  const handleAddPrepay = () => {
    if (dealId && data && dealPrepay) {
      PrivateData.addPayment(dealId, dealPrepay, 'prepay', dealCashback).then((item) => {
        if (item.redirect_url) {
          window.location.href = item.redirect_url;
        }
      });
    }
  };

  const handleAddFullpay = () => {
    if (dealId && data && dealFullpay) {
      PrivateData.addPayment(dealId, dealFullpay, 'fullpay', dealCashback).then((item) => {
        if (item.redirect_url) {
          window.location.href = item.redirect_url;
        }
      });
    }
  };

  const handleAddBonuspay = () => {
    if (dealId && data && bonus && dealFullpay) {
      PrivateData.addPayment(dealId, dealFullpay, 'fullpay', dealCashback, true).then((item) => {
        if (item.return_url) {
          window.location.href = item.return_url;
        }
      });
    }
  };

  useEffect(() => {
    if (isLogin !== null && isLogin && dealId === undefined) {
      const getQueryParam = (name: string): string | null => {
        return new URLSearchParams(window.location.search).get(name);
      };
      const dataString = getQueryParam('id');
      if (dataString) {
        try {
          PrivateData.getPrivateUserDeal(Number(dataString))
            .then((resp) => {
              if (resp.good) {
                setDealId(resp.id);
                setDealBegin(resp.begindate);
                setDealReserveBegin(resp.reserve_begin);
                setDealEnd(resp.closedate);
                setDealTotal(resp.total);
                setDealSurcharge(resp.surcharge);
                setDealCashback(resp.cashback);
                setDealTimeout(resp.timeout);
                setDealBonus(resp.bonus);
                setDealDiscount(resp.discount);
                setDealPrepay(resp.prepay);
                setDealFullpay(resp.fullpay);
                setDealStageId(resp.stage_id);
                if (resp.manager) {
                  setDealManager(resp.manager);
                }
                setProductId(resp.product_id);
                console.log(dealId, dealStageId);
              } else {
                console.log('Сделка не на нормальной стадии');
              }
            })
            .catch((error) => {
              console.log(error);
            });
        } catch (e) {
          console.error('Ошибка обработки сделки:', e);
        }
      }
    }
  }, [isLogin, dealId]);

  return (
    <Container>
      <Modal open={isOpen} handleClose={handleCloseModal}>
        <h3>Внимание!</h3>
        <p>
          Бесконтактное заселение в квартиру доступно только для верифицированных пользователей.
          Верификация доступна в{' '}
          <Link to="/account#proof-of-identity" className={payment('link-modal')}>
            личном кабинете
          </Link>
          .
        </p>
        <Button text="Ок" handleExternal={handleCloseModal} />
      </Modal>

      <div className={payment('')}>
        {dealStageId === 'PREPAYMENT_INVOICE' && (
          <div>
            <h2 className={payment('title')}>Внесение залога</h2>
            <p className={payment('deposit-info')}>
              Залог нужен для гарантий сохранности квартиры и подтверждения бронирования. Он
              возвращается в течение суток после окончания проживания.
            </p>
          </div>
        )}
        {dealStageId === '13' && (
          <div>
            <h2 className={payment('title')}>Внесение аванса</h2>
            <p className={payment('deposit-info')}>
              После внесения аванса Вам будет доступен код замка для входа в квартиру.
            </p>
          </div>
        )}
        <>
          {accommodation ? (
            <div className={payment('buttons-wrap')}>
              <AdvantagesSticker
                data={['Бесконтактное заселение']}
                onClick={() => setAccommodation(true)}
                crossedOut={!accommodation}
              />
            </div>
          ) : (
            <div className={payment('buttons-wrap')}>
              <AdvantagesSticker
                data={['Обычное заселение']}
                onClick={() => setAccommodation(false)}
                crossedOut={accommodation}
              />
            </div>
          )}
          <div className={payment('content')}>
            <div className={payment('info-box')}>
              {isLoading && (
                <ApartamentPayment
                  data={data}
                  cashback={dealCashback}
                  total={dealTotal}
                  begin={dealBegin}
                  end={dealEnd}
                  surcharge={dealSurcharge}
                  discount={dealDiscount}
                  timeout={dealTimeout}
                  manager={dealManager}
                />
              )}
            </div>
            <div className={payment('pay-way')}>
              {dealStageId === 'PREPAYMENT_INVOICE' && data && data.deposit && dealTimeout && (
                <>
                  <div className={payment('panel')}>
                    <h3 className={payment('deposit-header')}>
                      Необходимо внести {data.deposit} ₽
                    </h3>
                    <h3 className={payment('deposit-header')}>до {formattedDate(dealTimeout)}</h3>
                    <div className={payment('button-purchase')}>
                      <Button
                        externalClassName={payment('button')}
                        text="Оплатить залог"
                        handleExternal={handleAddDeposit}
                      />
                      <span className={payment('description')}>
                        Залог будет возвращен после окончания проживания в течение суток
                      </span>
                    </div>
                  </div>
                </>
              )}
              {dealStageId === '13' && data && dealFullpay && dealReserveBegin && dealPrepay && (
                <>
                  <div className={payment('panel')}>
                    <h3 className={payment('deposit-header')}>
                      Необходимо оплатить аванс {dealPrepay} ₽ за проживание до{' '}
                      {formattedDate(dealReserveBegin)}
                    </h3>
                    <div className={payment('button-purchase')}>
                      <Button
                        externalClassName={payment('button')}
                        text="Оплатить аванс"
                        handleExternal={handleAddPrepay}
                      />
                      <span className={payment('description')}>
                        После оплаты аванса Вам будет доступен код для доступа в квартиру
                      </span>
                    </div>
                  </div>
                </>
              )}
              {dealStageId === '16' && data && (
                <>
                  <div className={payment('panel')}>
                    <h3 className={payment('deposit-header')}>
                      Оплата за проживание: {dealFullpay} ₽
                    </h3>
                    <div className={payment('button-purchase')}>
                      <Button
                        externalClassName={payment('button')}
                        text="Оплатить"
                        handleExternal={handleAddFullpay}
                      />
                      <span className={payment('description')}>
                        После полной оплаты будут начислены бонусные баллы {dealBonus} ₽, которые
                        могут быть зачтены при следующих бронированиях.
                      </span>
                    </div>
                  </div>
                </>
              )}
              {dealStageId === '16' && data && dealFullpay && bonus >= dealFullpay && (
                <>
                  <div className={payment('panel')}>
                    <h3 className={payment('deposit-header')}>
                      У Вас есть бонусные баллы: {bonus} ₽
                    </h3>
                    <div className={payment('button-purchase')}>
                      <Button
                        externalClassName={payment('button')}
                        text="Оплатить бонусными баллами"
                        handleExternal={handleAddBonuspay}
                      />
                    </div>
                  </div>
                </>
              )}
              {dealCashback && (dealStageId === '14' || dealStageId === '16') && (
                <div className={payment('panel-cashback')}>
                  <h3 className={payment('pay-header')}>
                    Кэшбек: <span className={payment('discount')}> {dealDiscount} ₽</span>
                  </h3>
                  <AppInput
                    inputId="cashbackCard"
                    text="Номер карты для получения кэшбека"
                    type={TEXT}
                    externalClassName={payment('field')}
                    handleExternal={handleSetBonus}
                    validate="Неправильный номер карты"
                    valueExternal={bonusCard}
                    isValidExternal={bonusCardIsValid}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      </div>
    </Container>
  );
};

export default Payment;
